/**
 * Settings → Readiness: one page for every rule about whether an item is ready.
 *
 * Two layers (canon 24 §3 — Readiness includes Completeness):
 *   1. Every item — what every item must have (EveryItemRulesSection,
 *      workspace_definition_policy). This replaced "Terms & Definitions".
 *   2. Ready for ranking — named readiness definitions; the workspace has one,
 *      and a cycle or track may pick another (track ?? cycle ?? workspace).
 *      Every definition includes the Hard Stops from layer 1.
 *
 * All screens read both through lib/itemReadiness.ts.
 */

import { useState, useEffect } from 'react';
import { useTemplateAgents } from '../../../hooks/useTemplateAgents';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Trash2,
  CheckSquare,
  BarChart3,
  Bot,
  Save,
  X,
  Info,
  CheckCircle2,
  Pencil,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { Field, Select, TextArea, TextInput } from '../../../components/ds';
import { btnPrimary, btnSecondary } from '../../../components/mono/primitives';
import { EveryItemRulesSection } from './EveryItemRulesSection';
import { useReadinessDefinitionStore } from '../../../stores/prioritization/readinessDefinitionStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { STRUCTURAL_FIELD_METAS } from '../../../types/prioritization/definitionPolicy';
import type {
  ReadinessDefinition,
  ReadinessCriterion,
} from '../../../types/prioritization/readinessDefinition';

// ── Criterion type labels ─────────────────────────────────────────────────────

const CRITERION_TYPE_META = [
  {
    type: 'field_populated' as const,
    label: 'Field populated',
    description: 'A structural item field must be set (e.g. Owner, Altitude, Category)',
    icon: CheckSquare,
    color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]',
  },
  {
    type: 'attribute_values' as const,
    label: 'Attribute value',
    description: 'A scoring attribute must have an accepted value',
    icon: BarChart3,
    color: 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]',
  },
  {
    type: 'agent_signoff' as const,
    label: 'Agent sign-off',
    description: 'An AI agent must have reviewed and signed off on the item',
    icon: Bot,
    color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]',
  },
];

// The agents that can sign off: every agent on the template. An agent signs off on
// an item when a person accepts its output there (agent_signoffs).

// ── Criterion badge ───────────────────────────────────────────────────────────

function CriterionBadge({ criterion, onRemove }: { criterion: ReadinessCriterion; onRemove?: () => void }) {
  const meta = CRITERION_TYPE_META.find((m) => m.type === criterion.type);
  const Icon = meta?.icon ?? CheckSquare;

  let label = '';
  if (criterion.type === 'field_populated') label = criterion.label ?? criterion.fieldKey;
  if (criterion.type === 'attribute_values') label = criterion.label ?? criterion.attributeKey;
  if (criterion.type === 'agent_signoff') label = criterion.label ?? criterion.agentKey;

  return (
    <div className={`flex items-center gap-2  border px-3 py-2 text-sm ${meta?.color ?? ''} border-current/20`}>
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />
      <span className="font-medium">{label}</span>
      <span className="text-xs opacity-60">({meta?.label})</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1 rounded p-0.5 opacity-50 hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ── Add criterion panel ───────────────────────────────────────────────────────

function AddCriterionPanel({
  onAdd,
  onCancel,
  attributes,
}: {
  onAdd: (c: ReadinessCriterion) => void;
  onCancel: () => void;
  attributes: Array<{ key: string; label: string }>;
}) {
  const [type, setType] = useState<ReadinessCriterion['type']>('field_populated');
  const [fieldKey, setFieldKey] = useState(STRUCTURAL_FIELD_METAS[1]?.fieldKey ?? '');
  const [attributeKey, setAttributeKey] = useState(attributes[0]?.key ?? '');
  const templateAgents = useTemplateAgents();
  const knownAgents = templateAgents.map(({ def }) => ({ key: def.agentType, label: def.name }));
  const [agentKeyChoice, setAgentKey] = useState('');
  const agentKey = agentKeyChoice || knownAgents[0]?.key || '';
  const [requiredValues, setRequiredValues] = useState('');

  function handleAdd() {
    if (type === 'field_populated') {
      if (!fieldKey) return;
      const meta = STRUCTURAL_FIELD_METAS.find((m) => m.fieldKey === fieldKey);
      onAdd({ type: 'field_populated', fieldKey, label: meta?.label });
    } else if (type === 'attribute_values') {
      if (!attributeKey) return;
      const vals = requiredValues.split(',').map((v) => v.trim()).filter(Boolean);
      const attr = attributes.find((a) => a.key === attributeKey);
      onAdd({
        type: 'attribute_values',
        attributeKey,
        label: attr?.label,
        ...(vals.length > 0 && { requiredValues: vals }),
      });
    } else if (type === 'agent_signoff') {
      if (!agentKey) return;
      const agent = knownAgents.find((a) => a.key === agentKey);
      onAdd({ type: 'agent_signoff', agentKey, label: agent?.label });
    }
  }

  return (
    <div className="mt-3 border border-[var(--rule)] bg-[var(--surface)] p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
        Add criterion
      </p>

      {/* Type selector */}
      <div className="mb-4 flex flex-wrap gap-2">
        {CRITERION_TYPE_META.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.type}
              type="button"
              onClick={() => setType(m.type)}
              className={[
                'flex items-center gap-1.5  border px-3 py-1.5 text-sm font-medium transition-colors',
                type === m.type
                  ? `${m.color} border-current/30`
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]',
              ].join(' ')}
            >
              <Icon className="h-3.5 w-3.5" />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Type-specific config */}
      <div className="space-y-3">
        {type === 'field_populated' && (
          <Field label="Field" hint={STRUCTURAL_FIELD_METAS.find((m) => m.fieldKey === fieldKey)?.description}>
            <Select
              size="sm"
              value={fieldKey}
              onChange={(e) => setFieldKey(e.target.value)}
              options={STRUCTURAL_FIELD_METAS.filter((m) => !m.alwaysBlock).map((m) => ({ value: m.fieldKey, label: m.label }))}
            />
          </Field>
        )}

        {type === 'attribute_values' && (
          <>
            {attributes.length === 0 ? (
              <p className="text-xs text-[var(--text-ter)]">No scoring attributes defined yet.</p>
            ) : (
              <Field label="Attribute">
                <Select
                  size="sm"
                  value={attributeKey}
                  onChange={(e) => setAttributeKey(e.target.value)}
                  options={attributes.map((a) => ({ value: a.key, label: a.label }))}
                />
              </Field>
            )}
            <Field
              label="Required values (optional, comma-separated)"
              hint="Leave blank to accept any value. Enter values to require one of them."
            >
              <TextInput
                size="sm"
                value={requiredValues}
                onChange={(e) => setRequiredValues(e.target.value)}
                placeholder="e.g. High, Critical"
              />
            </Field>
          </>
        )}

        {type === 'agent_signoff' && (
          <Field label="Agent">
            <Select
              size="sm"
              value={agentKey}
              onChange={(e) => setAgentKey(e.target.value)}
              options={knownAgents.map((a) => ({ value: a.key, label: a.label }))}
            />
          </Field>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} {...btnSecondary()}>
          Cancel
        </button>
        <button type="button" onClick={handleAdd} {...btnPrimary('flex items-center gap-1.5')}>
          <Plus className="h-3.5 w-3.5" />
          Add criterion
        </button>
      </div>
    </div>
  );
}

// ── Definition editor ─────────────────────────────────────────────────────────

function DefinitionEditor({
  definition,
  attributes,
  onSave,
  onCancel,
  onDelete,
  isSaving,
  deleteNote,
}: {
  definition: Partial<ReadinessDefinition>;
  attributes: Array<{ key: string; label: string }>;
  onSave: (patch: { name: string; description: string; criteria: ReadinessCriterion[] }) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isSaving: boolean;
  /** Shown instead of Delete when the person may not delete a set. */
  deleteNote?: string;
}) {
  const [name, setName] = useState(definition.name ?? '');
  const [description, setDescription] = useState(definition.description ?? '');
  const [criteria, setCriteria] = useState<ReadinessCriterion[]>(definition.criteria ?? []);
  const [showAddCriterion, setShowAddCriterion] = useState(false);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5 shadow-sm">
      <div className="mb-4 space-y-3">
        <Field label="Name" required>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sprint Ready, Ranked, Backlog Standard"
          />
        </Field>
        <Field label="Description (optional)">
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="When is this used? What does it require?"
            rows={2}
          />
        </Field>
      </div>

      {/* Criteria list */}
      <div className="mb-1">
        <p className="mb-2 text-sm font-medium text-[var(--text-sec)]">
          Criteria{' '}
          <span className="font-normal text-[var(--text-ter)]">
            ({criteria.length} {criteria.length === 1 ? 'item' : 'items'})
          </span>
        </p>
        {criteria.length === 0 && (
          <p className="border border-dashed border-[var(--rule)] px-4 py-3 text-sm text-[var(--text-ter)]">
            No criteria of its own yet. It still requires every Hard Stop from Every item.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {criteria.map((c, i) => (
            <CriterionBadge
              key={i}
              criterion={c}
              onRemove={() => setCriteria((prev) => prev.filter((_, j) => j !== i))}
            />
          ))}
        </div>
        {showAddCriterion ? (
          <AddCriterionPanel
            attributes={attributes}
            onAdd={(c) => {
              setCriteria((prev) => [...prev, c]);
              setShowAddCriterion(false);
            }}
            onCancel={() => setShowAddCriterion(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowAddCriterion(true)}
            className="mt-3 flex items-center gap-1.5 border border-dashed border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-ter)] hover:border-[var(--slate)] hover:text-[var(--text-ter)]"
          >
            <Plus className="h-3.5 w-3.5" />
            Add criterion
          </button>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-[var(--rule)] pt-4">
        <div>
          {!onDelete && deleteNote && <p className="text-xs text-[var(--text-ter)]">{deleteNote}</p>}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)]"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} {...btnSecondary()}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim() || isSaving}
            onClick={() => onSave({ name: name.trim(), description, criteria })}
            {...btnPrimary('flex items-center gap-1.5 disabled:opacity-50')}
          >
            <Save className="h-3.5 w-3.5" />
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Definition card (read view) ───────────────────────────────────────────────

function DefinitionCard({
  definition,
  isWorkspaceDefault,
  onEdit,
  onMakeWorkspaceDefault,
}: {
  definition: ReadinessDefinition;
  isWorkspaceDefault: boolean;
  onEdit: () => void;
  onMakeWorkspaceDefault: () => void;
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-[var(--green)]" />
            <h3 className="truncate text-sm font-semibold text-[var(--text-pri)]">{definition.name}</h3>
            <span className="shrink-0 rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs text-[var(--text-ter)]">
              {definition.criteria.length} {definition.criteria.length === 1 ? 'criterion' : 'criteria'}
            </span>
            {isWorkspaceDefault && (
              <span className="shrink-0 rounded-full bg-[var(--green-dim)] px-2 py-0.5 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                Whole workspace
              </span>
            )}
          </div>
          {definition.description && (
            <p className="mt-1 text-xs text-[var(--text-ter)]">{definition.description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!isWorkspaceDefault && (
            <button type="button" onClick={onMakeWorkspaceDefault} {...btnSecondary('text-xs')}>
              Use for the whole workspace
            </button>
          )}
          <button type="button" onClick={onEdit} {...btnSecondary('flex items-center gap-1 text-xs')}>
            <Pencil className="h-3 w-3" />
            Edit
          </button>
        </div>
      </div>
      {definition.criteria.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {definition.criteria.map((c, i) => (
            <CriterionBadge key={i} criterion={c} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ReadinessDefinitions() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const workspaceDefinitionId = useReadinessDefinitionStore((s) => s.workspaceDefinitionId);

  const definitions = useReadinessDefinitionStore((s) => s.definitions);
  const loadForWorkspace = useReadinessDefinitionStore((s) => s.loadForWorkspace);
  const createDef = useReadinessDefinitionStore((s) => s.create);
  const updateDef = useReadinessDefinitionStore((s) => s.update);
  const deleteDef = useReadinessDefinitionStore((s) => s.delete);
  const setWorkspaceDefinition = useReadinessDefinitionStore((s) => s.setWorkspaceDefinition);
  const loading = useReadinessDefinitionStore((s) => s.loading);

  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  // Row security lets only a platform admin delete a set (migration 379), so
  // the Delete button is drawn only for one.
  const mayDelete = useUserStore((s) => s.currentRole === 'platform-admin');

  // ?definition=<id> opens that definition for editing (the readiness report links here).
  const [searchParams] = useSearchParams();
  const [editingId, setEditingId] = useState<string | 'new' | null>(searchParams.get('definition'));
  const [isSaving, setIsSaving] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // #every-item (the old Terms & Definitions address lands here) scrolls to layer 1.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  useEffect(() => {
    if (workspaceId) loadForWorkspace(workspaceId);
    fetchAttributes();
  }, [workspaceId, loadForWorkspace, fetchAttributes]);

  const attributeOptions = attributes.map((a) => ({ key: a.key, label: a.label ?? a.key }));

  async function handleCreate(patch: { name: string; description: string; criteria: ReadinessCriterion[] }) {
    if (!workspaceId) return;
    setIsSaving(true);
    const created = await createDef({
      workspaceId,
      name: patch.name,
      description: patch.description || undefined,
      criteria: patch.criteria,
      currentUserId: userId ?? undefined,
    });
    // A workspace with no definition of its own takes its first one.
    if (created && !workspaceDefinitionId) await setWorkspaceDefinition(created.id);
    setIsSaving(false);
    setEditingId(null);
  }

  async function handleUpdate(id: string, patch: { name: string; description: string; criteria: ReadinessCriterion[] }) {
    setIsSaving(true);
    await updateDef({
      id,
      name: patch.name,
      description: patch.description || null,
      criteria: patch.criteria,
    });
    setIsSaving(false);
    setEditingId(null);
  }

  async function handleDelete(id: string) {
    const warning =
      id === workspaceDefinitionId
        ? 'Delete this readiness definition? It is the one the whole workspace uses, so until you pick another, only the every-item rules will apply. This cannot be undone.'
        : 'Delete this readiness definition? Cycles and tracks that use it fall back to the workspace one. This cannot be undone.';
    if (!window.confirm(warning)) return;
    const ok = await deleteDef(id);
    if (!ok) {
      setAssignError('That set was not deleted. Nothing changed.');
      return;
    }
    setEditingId(null);
  }

  async function handleMakeWorkspaceDefault(id: string) {
    setAssignError(null);
    const ok = await setWorkspaceDefinition(id);
    if (!ok) setAssignError('Could not make that the workspace definition. Nothing changed.');
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <PageHeader
        title="Readiness"
        subtitle="Every rule about whether an item is ready, in one place. First what every item must have; then what an item needs to be ranked."
        icon={<CheckCircle2 className="h-5 w-5 text-[var(--green)]" />}
      />

      {/* Layer 1 */}
      <section id="every-item" className="scroll-mt-6">
        <h2 className="text-base font-semibold text-[var(--text-pri)]">1 · Every item</h2>
        <p className="mb-4 mt-1 max-w-3xl text-sm text-[var(--text-sec)]">
          What every item in this workspace must have, whatever it is used for. A Warning shows the
          gap; a Hard Stop keeps the item out of pools and ranking until it is filled in.
        </p>
        <EveryItemRulesSection />
      </section>

      {/* Layer 2 */}
      <section id="ready-for-ranking" className="mt-10 scroll-mt-6">
        <h2 className="text-base font-semibold text-[var(--text-pri)]">2 · Ready for ranking</h2>
        <p className="mb-4 mt-1 max-w-3xl text-sm text-[var(--text-sec)]">
          What an item needs before a track's pool can lock and the item can be ranked. Each set
          below always includes every Hard Stop from Every item, and adds its own criteria.
        </p>

        <div className="mb-6 flex items-start gap-3 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
          <div className="text-sm text-[var(--text-sec)]">
            <p className="font-medium">Which set applies</p>
            <p className="mt-0.5">
              The workspace uses the one marked <strong>Whole workspace</strong>. A cycle or a track can
              pick a different one in its settings; the most specific wins:{' '}
              <strong>Track → Cycle → Workspace</strong>.
            </p>
          </div>
        </div>

        {assignError && (
          <div className="mb-3 border border-[var(--coral)] bg-[var(--coral-dim)] p-3 text-sm text-[var(--coral)]">
            {assignError}
          </div>
        )}

        <div className="space-y-3">
          {loading && <p className="text-sm text-[var(--text-ter)]">Loading…</p>}

          {!loading && definitions.length === 0 && editingId !== 'new' && (
            <div className="border border-dashed border-[var(--rule)] px-6 py-10 text-center">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-[var(--text-ter)]" />
              <p className="text-sm font-medium text-[var(--text-ter)]">No sets for ranking yet</p>
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                Until there is one, an item is ready to rank when it meets every Hard Stop above.
              </p>
              <button
                type="button"
                onClick={() => setEditingId('new')}
                {...btnPrimary('mx-auto mt-4 flex items-center gap-1.5')}
              >
                <Plus className="h-3.5 w-3.5" />
                Create a set
              </button>
            </div>
          )}

          {definitions.map((def) =>
            editingId === def.id ? (
              <DefinitionEditor
                key={def.id}
                definition={def}
                attributes={attributeOptions}
                onSave={(patch) => handleUpdate(def.id, patch)}
                onCancel={() => setEditingId(null)}
                onDelete={mayDelete ? () => handleDelete(def.id) : undefined}
                deleteNote="Deleting a set needs a platform admin. To stop using it, pick another set for the workspace."
                isSaving={isSaving}
              />
            ) : (
              <DefinitionCard
                key={def.id}
                definition={def}
                isWorkspaceDefault={def.id === workspaceDefinitionId}
                onEdit={() => setEditingId(def.id)}
                onMakeWorkspaceDefault={() => void handleMakeWorkspaceDefault(def.id)}
              />
            )
          )}

          {editingId === 'new' && (
            <DefinitionEditor
              definition={{}}
              attributes={attributeOptions}
              onSave={handleCreate}
              onCancel={() => setEditingId(null)}
              isSaving={isSaving}
            />
          )}
        </div>

        {editingId !== 'new' && definitions.length > 0 && (
          <button
            type="button"
            onClick={() => setEditingId('new')}
            className="mt-4 flex items-center gap-1.5 border border-dashed border-[var(--rule)] px-4 py-2 text-sm text-[var(--text-ter)] hover:border-[var(--slate)]"
          >
            <Plus className="h-3.5 w-3.5" />
            Add a set
          </button>
        )}
      </section>
    </div>
  );
}
